import { useState, useEffect, useMemo } from 'react';
import { RefreshCw, FolderKanban, Calendar, Building2, AlertTriangle } from 'lucide-react';
import type { ProjectoCampaign, ProjectoInvoice, ProjectoPLItem, ProjectoPLSummary, ProjectoStatus } from '../../types/projecto';
import { PROJECTO_STATUS_CONFIG } from '../../types/projecto';
import {
  computeProjectoPLSummary,
  projectoCampaignService,
  projectoInvoiceService,
  projectoPLService,
} from '../../services/projectoService';
import { useModal } from '../../contexts/ModalContext';
import { formatEuro } from '../../utils/currency';
import { log } from '../../lib/logger';

function StatusBadge({ status }: { status: ProjectoStatus }) {
  const cfg = PROJECTO_STATUS_CONFIG[status];
  return (
    <span className={`cm-chip ${cfg.bg} ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

const STATUS_ORDER: ProjectoStatus[] = ['Active', 'Draft', 'On Hold', 'Completed', 'Cancelled'];

interface ProjectoListProps {
  externalProjects?: ProjectoCampaign[];
  onEdit?: (project: ProjectoCampaign) => void;
  onRefresh?: () => void;
  searchFilter?: string;
}

export function ProjectoList({ externalProjects, onEdit, onRefresh, searchFilter }: ProjectoListProps) {
  const [internalProjects, setInternalProjects] = useState<ProjectoCampaign[]>([]);
  const [loading, setLoading] = useState(!externalProjects);
  const [statusFilter, setStatusFilter] = useState<ProjectoStatus | 'all'>('all');
  const [lines, setLines] = useState<ProjectoPLItem[]>([]);
  const [allInvoices, setAllInvoices] = useState<ProjectoInvoice[]>([]);
  const { showConfirm, toast } = useModal();

  const projects = externalProjects ?? internalProjects;

  // Refetched with the list, so a card is right after closing a project
  useEffect(() => {
    Promise.all([projectoPLService.getAll(), projectoInvoiceService.getAll()])
      .then(([items, invoices]) => { setLines(items); setAllInvoices(invoices); })
      .catch((err) => log.warn('Project margins not loaded:', err));
  }, [projects]);

  const summaries = useMemo(() => new Map(projects.map((p) => [
    p.id,
    computeProjectoPLSummary(
      lines.filter((l) => l.projecto_id === p.id),
      allInvoices.filter((i) => i.projecto_id === p.id),
      p,
    ),
  ])), [projects, lines, allInvoices]);

  const load = async () => {
    if (externalProjects) {
      onRefresh?.();
      return;
    }
    setLoading(true);
    try {
      const data = await projectoCampaignService.getAll();
      setInternalProjects(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!externalProjects) load();
  }, [externalProjects]);

  const filtered = useMemo(() => {
    let list = projects;
    if (statusFilter !== 'all') list = list.filter((p) => p.status === statusFilter);
    if (searchFilter?.trim()) {
      const q = searchFilter.toLowerCase();
      list = list.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        p.client_name.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q) ||
        p.reference.toLowerCase().includes(q) ||
        (p.work_code ?? '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [projects, searchFilter, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts: Partial<Record<ProjectoStatus, number>> = {};
    for (const p of projects) {
      counts[p.status] = (counts[p.status] ?? 0) + 1;
    }
    return counts;
  }, [projects]);

  const handleOpen = (project: ProjectoCampaign) => {
    onEdit?.(project);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await showConfirm({
      title: 'Eliminar projeto?',
      message: 'O projeto e tudo o que tem (linhas do P&L, faturas, histórico) serão eliminados. Esta ação não pode ser revertida.',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await projectoCampaignService.delete(id);
      toast.success('Projeto eliminado');
      load();
    } catch {
      toast.error('Não foi possível eliminar o projeto');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            statusFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Todos ({projects.length})
        </button>
        {STATUS_ORDER.map((s) => {
          const cfg = PROJECTO_STATUS_CONFIG[s];
          const count = statusCounts[s] ?? 0;
          if (count === 0) return null;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === s ? `${cfg.bg} ${cfg.color}` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cfg.label} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-6 h-6 text-gray-300 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <FolderKanban className="w-12 h-12 mb-3 opacity-20" />
  XX
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((project) => (
            <ProjectoCard
              key={project.id}
              project={project}
              summary={summaries.get(project.id)}
              onOpen={() => handleOpen(project)}
              onDelete={() => handleDelete(project.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CardProps {
  project: ProjectoCampaign;
  summary?: ProjectoPLSummary;
  onOpen: () => void;
  onDelete: () => void;
}

function ProjectoCard({ project, summary, onOpen, onDelete }: CardProps) {
  return (
    <div
      className="bg-white rounded-card shadow-card ring-1 ring-gray-100 p-5 hover:shadow-card-hover transition-all cursor-pointer group relative flex flex-col"
      onClick={onOpen}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{project.name || 'Sem nome'}</h3>
          <div className="flex items-center gap-2">
            {project.work_code && <span className="text-xs text-gray-500 font-mono">{project.work_code}</span>}
            {project.reference && <span className="text-xs text-gray-500 font-mono truncate">{project.reference}</span>}
          </div>
        </div>
        <StatusBadge status={project.status} />
      </div>

      <div className="space-y-1.5 text-xs text-gray-500">
        {project.client_name && (
          <div className="flex items-center gap-1.5">
            <Building2 className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{project.client_name}{project.brand ? ` · ${project.brand}` : ''}</span>
          </div>
        )}
        {(project.start_date || project.finish_date) && (
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3 flex-shrink-0" />
            <span>
              {project.start_date ? new Date(project.start_date).toLocaleDateString('pt-PT') : '—'}
              {' → '}
              {project.finish_date ? new Date(project.finish_date).toLocaleDateString('pt-PT') : '—'}
            </span>
          </div>
        )}
      </div>

      {summary && (
        <dl className="mt-auto pt-4 grid grid-cols-3 gap-2" data-testid="projecto-card-figures">
          <div className="min-w-0">
            <dt className="text-[11px] text-gray-500 truncate">{summary.fixedBudget ? 'Orçamento' : 'Cliente paga'}</dt>
            <dd className="text-sm font-semibold text-gray-900 tabular-nums truncate">{formatEuro(summary.revenue)}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[11px] text-gray-500">Custos</dt>
            <dd className="text-sm font-semibold text-gray-900 tabular-nums truncate">{formatEuro(summary.costs)}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[11px] text-gray-500">Margem</dt>
            <dd className={`text-sm font-semibold tabular-nums truncate ${summary.margin < 0 ? 'text-red-700' : summary.margin > 0 ? 'text-emerald-700' : 'text-gray-500'}`}>{formatEuro(summary.margin)}</dd>
          </div>
        </dl>
      )}

      <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-100 min-h-[2.25rem]">
        {summary && summary.missingCostLines > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs text-amber-800">
            <AlertTriangle className="w-3.5 h-3.5" />
            {summary.missingCostLines === 1 ? '1 linha sem custo' : `${summary.missingCostLines} linhas sem custo`}
          </span>
        ) : (
          <span className="text-xs text-gray-500">
            {summary?.fixedBudget ? 'Orçamento fechado' : 'Custos + comissão'}
          </span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-xs text-red-600 hover:text-red-800 transition-colors font-medium sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
        >
          Eliminar
        </button>
      </div>
    </div>
  );
}
